<svelte:options />

<script>

    import { onMount, getContext } from 'svelte';
    import jq from 'jquery';
    import { getTenantCachedImageFromString, resolveStorageUrl, resolvePlaceholder } from '@repo/config/Storage';
    import md5 from 'md5';
    import { contentActions, roles, notificationTypes } from '@repo/config/Global';
    import { PREVIEW_MODE_DOWNLOAD_MESSAGE, isPreviewUser } from '@repo/config/previewMode';
    import { COLLECTION_TYPES } from '@repo/content-db';
    import VisibilityBadge from '../VisibilityBadge.svelte';
    import { CONTENT_VISIBILITY_CONTEXT } from '../contentVisibilityContext.js';

    const visibilityCtx = getContext(CONTENT_VISIBILITY_CONTEXT);
    import { getYTIdFromURL, getUniqueCodeID } from '@repo/config/Utils';
    import { goto } from '$app/navigation';
    import { eventBus } from '@repo/stores/EventBus';
    import { toast } from '@repo/stores/Messaging';
    import { page } from '$app/state';
    import { getBookAccordionDeps } from '../context.js';

    const { access, db, functions, features } = getBookAccordionDeps();
    const isContentVisibilityEnabled = features?.isContentVisibilityEnabled ?? (() => false);
    const {
        hasContentAction,
        hasRole,
        canAccessTeacherOnlyMaterial,
        canAccessResource,
        hasBookAccess = () => false,
        currentUser,
    } = access;
    const { getEntity, getEntityBreadcrumb } = db;
    const callable = functions?.callable ?? null;
    /**
     * @typedef {Object} Props
     * @property {any} data
     * @property {string} [label]
     * @property {boolean} [active]
     * @property {boolean} [armed]
     * @property {boolean} [disabled]
     * @property {boolean} [isHidden]
     * @property {boolean} [parentHidden]
     */
    let {
        data = $bindable(),
        label = '',
        active = false,
        armed = false,
        disabled = false,
        isHidden = false,
        parentHidden = false
    } = $props();

    const THUMBNAIL_LOADING = '/ui/loader.svg';
    const THUMBNAIL_PLACEHOLDER = 'placeholder';

    let visibilityPending = $derived(
        !!data?.id && visibilityCtx?.pendingIds?.has?.(data.id) === true
    );

    // A resource is effectively hidden when flagged itself OR when any
    // ancestor section is hidden — hiding a parent hides the whole subtree.
    let effectiveHidden = $derived(isHidden || parentHidden === true);

    // Teachers see hidden resources dimmed/disabled; students never render
    // hidden resources at all, so the access guard is belt-and-braces.
    let showAsHidden = $derived(effectiveHidden && isContentVisibilityEnabled() && canAccessTeacherOnlyMaterial());

    /**
     * Resolve the parent Title id this resource belongs to. Prefer `material`
     * (set after the breadcrumb lookup completes) and fall back to parsing
     * docPath, which is set by BookAccordianItem.getSubItems.
     */
    function resolveTitleId() {
        if (material) return material;
        if (data?._collectionId) return data._collectionId;
        const parts = (data?.docPath || '').split('/');
        return parts[1] || null;
    }

    function handleVisibilityToggle() {
        if (!visibilityCtx || !data) return;
        const titleId = resolveTitleId();
        if (!titleId) return;
        visibilityCtx.toggleItem({
            collectionId: titleId,
            itemId: data.id,
            docPath: data.docPath || null,
            currentlyHidden: effectiveHidden,
        });
    }

    let background = $state('');
    let thumbBackground = $state(THUMBNAIL_PLACEHOLDER);
    const isPlaceholderThumbnail = $derived(thumbBackground === THUMBNAIL_PLACEHOLDER);
    let scaleThumb = $state(0);
    const maxTitleLength = 36;
    let localDisabled = $state(true);
    let released = $state(true);
    let forceRender = $state(null);
    let material = null;
    let downloading = $state(false);
    // currentUser.systemData is the live user doc, so toggling preview mode
    // updates this without a sign-out.
    let previewMode = $derived(isPreviewUser($currentUser));
    let placeholderUrl = $state('');

    const IN_APP_PREVIEWABLE_EXTENSIONS = new Set(['pdf']);

    const BROWSER_VIEWABLE_EXTENSIONS = new Set([
        'png',
        'jpg',
        'jpeg',
        'gif',
        'svg',
        'bmp',
        'webp',
        'txt',
        'html',
        'htm',
        'mp4',
        'webm',
        'mov',
        'mp3',
        'wav',
        'ogg'
    ]);

    eventBus.listen('documentViewer-thumbnail', (event) => {
        if (event.id === data.id && event.thumbnailURI) {
            resolveStorageUrl(event.thumbnailURI).then(url => {
                background = url;
            });
        }
    });

    $effect(() => {
        if (background) {
            // Don't overwrite if thumbBackground is already set to the resolved URL
            if (thumbBackground === THUMBNAIL_PLACEHOLDER || thumbBackground === THUMBNAIL_LOADING) {
                thumbBackground = THUMBNAIL_LOADING;
            }
        } else {
            thumbBackground = THUMBNAIL_PLACEHOLDER;
        }
    });

    function getMaterial() {
        return new Promise((resolve, reject) => {
            getEntityBreadcrumb({ id: data.id })
                .then((bc) => {
                    if (bc) {
                        for (const b in bc) {
                            if (bc[b].catalog === 'Title') material = bc[b].id;
                        }
                        resolve(bc);
                    } else {
                        reject(`No breadcrumb returned for ${data.id}`);  
                    }
                })
                .catch(reject);
        });
    }

    onMount(() => {
        // If only key in data is id, fetch full resource data
        if (Object.keys(data).length === 1 && 'id' in data) {
            getEntity({ id: data.id }, COLLECTION_TYPES.RESOURCES).then((res) => {
                const canAccessTeacherOnlyContent = canAccessTeacherOnlyMaterial();
                if (res.exists()) {
                    const intendedEndUserRole =
                        res.data().intendedEndUserRole || null;
                    if (
                        intendedEndUserRole === 'Teacher' &&
                        !canAccessTeacherOnlyContent
                    ) {
                        throw new Error('Teacher only content');
                    }

                    if (
                        res.data().thumbnailURI &&
                        res.data().thumbnailURI.length > 5 &&
                        res.data().thumbnailURI !== background
                    ) {
                        resolveStorageUrl(res.data().thumbnailURI).then(url => { background = url; });
                    }
                }

                if (!material) {
                    getMaterial()
                        .then(() => setTimeout(updateData))
                        .catch(console.error);
                } else {
                    setTimeout(updateData);
                }
            });
        } else {
            if (data.titleId && !material) {
                material = data.titleId;
            } else if (data._collectionId && !material) {
                material = data._collectionId;
            }

            if (data.thumbnailURI && data.thumbnailURI.length > 5) {
                resolveStorageUrl(data.thumbnailURI).then(url => { background = url; });
            }

            if (!material) {
                getMaterial()
                    .then(() => setTimeout(updateData))
                    .catch(console.error);
            } else {
                setTimeout(updateData);
            }
        }
    });

    async function updateData() {
        localDisabled = true;
        let willBeDisabled = true;

        switch (data.catalog) {
            case 'Presentation Media':
            case 'PresentationMedia':
                if (
                    material &&
                    hasContentAction(
                        contentActions.VIEW_PRESENTATION_MEDIA_RESOURCES,
                        material
                    )
                )
                    willBeDisabled = false;
                data.resourceTypeLabel = 'slides';
                break;
            case 'Page':
                { if (
                    material &&
                    ((hasRole(roles.STUDENT) &&
                        hasContentAction(
                            contentActions.VIEW_STUDENT_EDITION,
                            material
                        )) ||
                        ((hasRole(roles.TEACHER) ||
                            hasRole(roles.DISTRICT_ADMIN) ||
                            hasRole(roles.SCHOOL_ADMIN)) &&
                            hasContentAction(
                                contentActions.VIEW_TEACHER_EDITION,
                                material
                            )))
                )
                    willBeDisabled = false;
                data.resourceTypeLabel =
                    data.title.toLowerCase().indexOf('chapter') > -1
                        ? 'introduction'
                        : 'activity';
                const keywords = data.keywords || '';
                const m = keywords.match(/pg[0-9]{0,2}/g);  
                if (m) data.page = Number(m[0].split('pg')[1]);
                break; }
            case 'Weblinks':
                // handle sketchfab //
                if (data.location.indexOf('sketchfab') > -1) {
                    data.resourceTypeLabel = '3d model';
                    if (
                        material &&
                        hasContentAction(
                            contentActions.VIEW_3D_MODELS_RESOURCES,
                            material
                        )
                    )
                        willBeDisabled = false;
                    if (
                        !data.thumbnailURI ||
                        data.thumbnailURI.length < 8 ||
                        data.thumbnailURI.indexOf('sketchfab') < 0
                    ) {
                        jq.get(
                            `https://sketchfab.com/oembed?url=${data.location}`,
                            (res) => {
                                background = res.thumbnail_url;
                            }
                        );
                    }
                    // handle Youtube
                } else if (data.location.indexOf('youtu') > -1) {
                    if (
                        material &&
                        hasContentAction(
                            contentActions.VIEW_VIDEO_RESOURCES,
                            material
                        )
                    )
                        willBeDisabled = false;
                    data.resourceTypeLabel = 'video';
                    const yid = getYTIdFromURL(data.location);
                    background = `https://img.youtube.com/vi/${yid}/0.jpg`;
                    scaleThumb = 2.5;
                    // Handle all other links
                } else if (data.location) {
                    let ext =
                        data.location.split('.')[
                            data.location.split('.').length - 1
                        ] || '';
                    if (ext.length < 2) ext = '';
                    data.resourceSubType = ext;
                    data.resourceTypeLabel = 'download';

                    switch (ext) {
                        case 'xls':
                        case 'xlsx':
                            if (
                                material &&
                                hasContentAction(
                                    contentActions.VIEW_FILE_DOWNLOADS_RESOURCES,
                                    material
                                )
                            )
                                willBeDisabled = false;
                            background =
                                '//upload.wikimedia.org/wikipedia/commons/3/34/Microsoft_Office_Excel_%282019%E2%80%93present%29.svg';
                            scaleThumb = 1;
                            break;
                        case 'doc':
                        case 'docx':
                            if (
                                material &&
                                hasContentAction(
                                    contentActions.VIEW_FILE_DOWNLOADS_RESOURCES,
                                    material
                                )
                            )
                                willBeDisabled = false;
                            background =
                                '//upload.wikimedia.org/wikipedia/commons/f/fd/Microsoft_Office_Word_%282019%E2%80%93present%29.svg';
                            scaleThumb = 1;
                            break;
                        case 'ppt':
                        case 'pptx':
                            if (
                                material &&
                                hasContentAction(
                                    contentActions.VIEW_FILE_DOWNLOADS_RESOURCES,
                                    material
                                )
                            )
                                willBeDisabled = false;
                            background =
                                '//upload.wikimedia.org/wikipedia/commons/0/0d/Microsoft_Office_PowerPoint_%282019%E2%80%93present%29.svg';
                            scaleThumb = 1;
                            break;
                        case 'pdf':
                            if (
                                material &&
                                hasContentAction(
                                    contentActions.VIEW_FILE_DOWNLOADS_RESOURCES,
                                    material
                                )
                            )
                                willBeDisabled = false;
                            break;
                        case 'zip':
                            if (
                                material &&
                                hasContentAction(
                                    contentActions.VIEW_FILE_DOWNLOADS_RESOURCES,
                                    material
                                )
                            )
                                willBeDisabled = false;
                            background = 'media/icons/folder_zip.svg';
                            break;
                        default:
                            if (
                                material &&
                                hasContentAction(
                                    contentActions.VIEW_WEBLINKS_RESOURCES,
                                    material
                                )
                            )
                                willBeDisabled = false;

                            data.resourceTypeLabel = 'web link';

                            if (!data?.thumbnailURI) {
                                const fn = `img_${md5(
                                    encodeURI(data.location)
                                )}`;  
                                getTenantCachedImageFromString(fn)
                                    .then(async (res) => {
                                        if (res.length > 0) {
                                            background = res;
                                        } else {
                                            if (data?.lockThumbnail === true) {
                                                scaleThumb = 0;
                                            } else {
                                                const updateThumbnail =
                                                    callable('createCourseThumbnail');
                                                await updateThumbnail({
                                                    id: data.id,
                                                }).catch(console.error);

                                                getTenantCachedImageFromString(
                                                    fn
                                                )
                                                    .then((res) => {
                                                        if (res.length > 0) {
                                                            background = res;
                                                        }
                                                    })
                                                    .catch(console.error);
                                            }
                                        }
                                    })
                                    .catch(async (err) => {
                                        const linkIcon =
                                            'media/icons/link_icon.svg';
                                        switch (err.code) {
                                            case 'storage/object-not-found':
                                                if (
                                                    data?.lockThumbnail === true
                                                ) {
                                                    scaleThumb = 1;
                                                    background = linkIcon;
                                                } else {
                                                    const updateThumbnail =
                                                        callable('createCourseThumbnail');
                                                    await updateThumbnail({
                                                        id: data.id,
                                                    }).catch(console.error);

                                                    getTenantCachedImageFromString(
                                                        fn
                                                    )
                                                        .then((res) => {
                                                            if (
                                                                res.length > 0
                                                            ) {
                                                                background =
                                                                    res;
                                                            }
                                                        })
                                                        .catch((err) => {
                                                            console.error(err);
                                                            background =
                                                                linkIcon;
                                                            scaleThumb = 1;
                                                        });
                                                }
                                                break;
                                            case 'storage/unauthorized':
                                                console.debug(
                                                    'Unauthorized access to storage for ',
                                                    fn
                                                );
                                                break;
                                            default:
                                                background = linkIcon;
                                                scaleThumb = 1;
                                        }
                                    });
                            }

                            scaleThumb = 0;
                            break;
                    }
                }
                break;
        }

        // Resolve placeholder icon from bucket for this resource type
        resolvePlaceholder(data.catalog).then(url => {
            if (url) placeholderUrl = url;
        });

        // get title
        data.title = data.title || label;

        if ('released' in data && (data.released === false || data.released === 'false')) {
            released = false;
        }

        // Book-access override: title-level access (custom claims / subscription
        // book access) bypasses the catalog-level content-action + role checks.
        // Resolve the parent title from titleId, _collectionId, or docPath so it
        // also covers role-less users (e.g. standalone TAP) whose nested Page
        // resources only carry _collectionId, not a stored titleId field.
        const bookAccessId = data.titleId || data._collectionId || resolveTitleId();
        if (willBeDisabled && bookAccessId && hasBookAccess(bookAccessId)) {
            willBeDisabled = false;
        }

        localDisabled = willBeDisabled || disabled;
        if (!localDisabled) {
            if (data.titleId && hasBookAccess(data.titleId)) {
                localDisabled = false;
                forceRenderUpdate();
            } else if (data._collectionId) {
                localDisabled = !hasBookAccess(data._collectionId);
                forceRenderUpdate();
            } else {
                canAccessResource(data.id).then((res) => {
                    localDisabled = !res;
                    forceRenderUpdate();
                });
            }
        }

        forceRenderUpdate();
    }

    function forceRenderUpdate() {
        setTimeout(() => {
            forceRender = getUniqueCodeID();
        });
    }

    function handleResourceClick(e) {
        if (!localDisabled && released) {
            const deliveryMeta = getResourceDeliveryMeta(data);
            if (data.downloadable && !deliveryMeta.inAppPreviewable) {
                e.preventDefault();
                e.stopImmediatePropagation();
                handleDownload();
                return;
            }

            const { params } = page;
            const { classId } = params;
            if (classId) {
                goto(`/classroom/${classId}/teach/${data.id}`);
            } else {
                goto(`${data.id}`);
            }
        } else if (localDisabled || !released) {
            e.preventDefault();
            e.stopImmediatePropagation();

            const msg = localDisabled ? 'You do not have access to this resource' : 'This resource is not yet released';
            $toast = {
                type: notificationTypes.WARN,
                message: msg,
            };
        }
    }

    function handleImageError(e) {
        e.preventDefault();
        e.stopImmediatePropagation();
        background = '';
        thumbBackground = THUMBNAIL_PLACEHOLDER;
    }

    function handleImageLoad(e) {
        thumbBackground = background || THUMBNAIL_PLACEHOLDER;
    }

    function getResourceExtension(resource) {
        if (!resource) return '';

        if (resource.resourceSubType) {
            return String(resource.resourceSubType).toLowerCase();
        }

        const path = typeof resource.location === 'string' ? resource.location : '';
        if (!path) return '';

        const withoutQuery = path.split('?')[0].split('#')[0];
        if (!withoutQuery.includes('.')) return '';

        const ext = withoutQuery.split('.').pop();
        return ext ? ext.toLowerCase() : '';
    }

    function isInAppPreviewableExtension(extension) {
        return Boolean(extension && IN_APP_PREVIEWABLE_EXTENSIONS.has(extension));
    }

    function isBrowserViewableExtension(extension) {
        return Boolean(extension && BROWSER_VIEWABLE_EXTENSIONS.has(extension));
    }

    function getResourceDeliveryMeta(resource) {
        const extension = getResourceExtension(resource);
        const location = typeof resource?.location === 'string' ? resource.location : '';
        const isExternalLink = Boolean(
            location && (location.startsWith('https://') || location.startsWith('http://'))
        );

        const inAppPreviewable = isInAppPreviewableExtension(extension);
        const browserViewable =
            !inAppPreviewable &&
            (isBrowserViewableExtension(extension) || (!extension && isExternalLink));

        return {
            extension,
            isExternalLink,
            inAppPreviewable,
            browserViewable,
        };
    }

    function buildDownloadName(name, extension) {
        const fallback = 'download';
        const trimmed = (name || fallback).trim();
        const sanitized = Array.from(trimmed)
            .map((char) => {
                const code = char.charCodeAt(0);
                if (code < 32 || /[<>:"/\\|?*]/.test(char)) return '_';
                return char;
            })
            .join('');
        const baseName =
            sanitized.replace(/_{2,}/g, '_').replace(/^_+|_+$/g, '') || fallback;

        if (!extension) return baseName;

        const normalizedExt = extension.startsWith('.') ? extension.slice(1) : extension;

        if (baseName.toLowerCase().endsWith(`.${normalizedExt}`)) {
            return baseName;
        }

        return `${baseName}.${normalizedExt}`;
    }

    function renderPreviewPlaceholder(win, name) {
        if (!win) return;

        try {
            const safeName = name.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const doc = win.document;
            if (!doc) return;

            doc.open();
            doc.write(`<!doctype html>
<html lang="en">
    <head>
        <meta charset="utf-8" />
        <title>Loading ${safeName}</title>
        <style>
            :root { color-scheme: light dark; }
            body { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:100vh; margin:0; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; background:#0f172a; color:#e2e8f0; }
            .logo { width:160px; max-width:60vw; margin-bottom:24px; }
            .logo.light { display:none; }
            .logo.dark { display:block; }
            @media (prefers-color-scheme: light) {
                body { background:#f8fafc; color:#0f172a; }
                .logo.dark { display:none; }
                .logo.light { display:block; }
            }
            .spinner { width:48px; height:48px; border:4px solid rgba(148, 163, 184, 0.35); border-top-color:currentColor; border-radius:50%; animation:spin 0.9s linear infinite; margin-bottom:16px; }
            @keyframes spin { to { transform: rotate(360deg); } }
        </style>
    </head>
    <body>
        <img class="logo dark" src="/tenant/biozone-world-white.svg" alt="BIOZONE logo" />
        <img class="logo light" src="/tenant/biozone-world-black.svg" alt="BIOZONE logo" />
        <div class="spinner" aria-hidden="true"></div>
        <p>Preparing <strong>${safeName}</strong>&hellip;</p>
    </body>
</html>`);
            doc.close();
        } catch (err) {
            console.debug('Unable to render preview placeholder', err);
        }
    }

    function handleDownload() {
        if (previewMode) {
            $toast = {
                type: notificationTypes.WARN,
                message: PREVIEW_MODE_DOWNLOAD_MESSAGE,
            };
            return;
        }
        if (downloading) return; // Prevent multiple clicks
        downloading = true;

        const { location, id, title } = data;
        const deliveryMeta = getResourceDeliveryMeta(data);
        const { extension, isExternalLink, inAppPreviewable, browserViewable } = deliveryMeta;
        const downloadName = buildDownloadName(title, extension);
        let previewWindow = null;

        if (inAppPreviewable && typeof window !== 'undefined') {
            previewWindow = window.open('', '_blank');
            if (previewWindow) {
                previewWindow.opener = null;
                renderPreviewPlaceholder(previewWindow, downloadName);
            }
        }

        if (!location) {
            $toast = {
                type: notificationTypes.WARN,
                message: 'No download link available for this resource',
            };
            downloading = false;
            if (previewWindow) previewWindow.close();
            return;
        }

        const triggerDownload = (url, options = {}) => {
            const { previewWindowRef = null } = options;

            try {
                if ((inAppPreviewable || browserViewable) && typeof window !== 'undefined') {
                    if (previewWindowRef && !previewWindowRef.closed) {
                        previewWindowRef.location = url;
                    } else {
                        const newWindow = window.open(url, '_blank');
                        if (newWindow) {
                            newWindow.opener = null;
                        } else {
                            window.location.assign(url);
                        }
                    }
                } else {
                    const link = document.createElement('a');
                    link.href = url;
                    link.rel = 'noopener noreferrer';
                    link.target = '_self';
                    link.download = downloadName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();

                    $toast = {
                        type: notificationTypes.SUCCESS,
                        message: 'Download started',
                    };
                }
            } finally {
                downloading = false;
            }
        };

        if (isExternalLink) {
            triggerDownload(location, { previewWindowRef: previewWindow });
            return;
        }

        const getFileUrl = callable('getFileUrl');

        getFileUrl({ id, classId: page?.params?.classId || null })
            .then((result) => {
                if (result?.data) {
                    triggerDownload(result.data, { previewWindowRef: previewWindow });
                } else {
                    if (previewWindow) previewWindow.close();
                    throw new Error('No download URL returned from getFileUrl');
                }
            })
            .catch((error) => {
                if (previewWindow) previewWindow.close();
                console.error('Download error:', error);
                $toast = {
                    type: notificationTypes.ERROR,
                    message: error.code === 'functions/resource-exhausted'
                        ? 'You\'ve made too many requests. Please try again later.'
                        : 'Error retrieving download link',
                };
            }).finally(() => {
                downloading = false;
            });
    }


    export {
        data,
        label,
        active,
        armed,
        disabled,
    };
</script>

{#key forceRender}
    {#if data}
        <div
            id={'el_' + data.id}
            title={localDisabled
                ? 'Not available on your subscription level. Please contact BIOZONE for information'
                : data.title}
            class="resource-item {localDisabled ? 'disabled' : ''} {data.catalog
                .toLowerCase()
                .split(' ')
                .join('-')} {data.resourceTypeLabel
                    ? data.resourceTypeLabel.toLowerCase().split(' ').join('-')
                    : ''}
        {active ? 'active' : ''} {armed ? 'armed' : ''} {showAsHidden ? 'hidden-from-students' : ''}"
        >
            {#if active}
                <span class="sr-only">Current Resource</span>
            {/if}
            <button
                onclick={handleResourceClick}
                onkeypress={handleResourceClick}
                class="row stretch interactive"
            >
                <!-- hidden img holder to test thumb -->
                {#if background !== ''}
                    {#key background}
                        <img
                            src={background}
                            onerror={handleImageError}
                            onload={handleImageLoad}
                            width="100"
                            height="100"
                            style="display:none;"
                            alt={data.title + ' page thumbnail'}
                        />
                    {/key}
                {/if}
                <div
                    class="thumbnail thumbnail-{data.id} {isPlaceholderThumbnail ? 'placeholder' : ''}"
                    style="--placeholder-icon:url('{placeholderUrl}');background-image:url('{thumbBackground}');background-position:{scaleThumb ===
                    0
                        ? 'center top'
                        : 'center center'};background-repeat:no-repeat;background-size:{scaleThumb ===
                    0
                            ? 'cover'
                            : `${scaleThumb * 75}%`};"
                ></div>
                <span>
                    {#if !released}
                        <span class="currently-not-available">
                            { data?.notReleasedTitle || 'Currently not available...' }
                        </span>
                    {:else}
                        {#if data.resourceTypeLabel}
                            <small>{data.overrideCardKeyword || data.overrideCardKeyword || data.resourceTypeLabel || ''}</small>
                        {/if}
                        {#if data.title.length > maxTitleLength}
                            {data.title.substring(0, maxTitleLength)}&mldr;
                        {:else}
                            {data.title}
                        {/if}
                    {/if}
                </span>
            </button>

            <span class="resource-visibility-slot">
                <VisibilityBadge
                    hidden={effectiveHidden}
                    onclick={visibilityCtx ? handleVisibilityToggle : null}
                    pending={visibilityPending}
                />
            </span>

            {#if data.downloadable && released}
                <button
                    onclick={handleDownload}
                    class="interactive resource-download {previewMode ? 'preview-disabled' : ''}"
                    title={previewMode ? PREVIEW_MODE_DOWNLOAD_MESSAGE : 'Download Activity'}
                    aria-label={previewMode ? PREVIEW_MODE_DOWNLOAD_MESSAGE : 'Download Activity'}
                    aria-disabled={previewMode}
                >
                    {#if downloading}
                        <span class="sr-only">Downloading...</span>
                        <img src="/ui/loader.svg" alt="Loading" />
                    {:else}
                        <span class="sr-only">Download Resource</span>
                        <i class="material-icons icon-button" aria-hidden="true">download</i>
                    {/if}
                    
                </button>
            {/if}
        </div>
    {/if}
{/key}

<style>
    .resource-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 10;
        position: relative;
        background-color: rgba(255, 255, 255, 0.05);
        border-radius: 0.25rem;
    }

    .resource-visibility-slot {
        display: inline-flex;
        align-items: center;
        align-self: center;
        margin-left: auto;
        margin-right: 0.5rem;
        flex-shrink: 0;
        z-index: 2;
    }

    /* When the resource card is selected/armed it switches to a bright yellow
       background. Override the badge palette via :global() so the icon stays
       legible on top of #ffce2c. */
    .resource-item.active :global(.visibility-badge--off),
    .resource-item.armed :global(.visibility-badge--off) {
        background-color: #b71c1c !important;
        color: #fff !important;
    }

    .resource-item.active :global(.visibility-badge--on),
    .resource-item.armed :global(.visibility-badge--on) {
        background-color: rgba(0, 0, 0, 0.2) !important;
        color: rgba(0, 0, 0, 0.75) !important;
    }

    .resource-item.active,
    .resource-item.armed {
        margin-right: 0;
        padding-right: 0.5rem;
        background-color: #ffce2c;
    }

    /* Teacher view of a resource hidden from students: dim the card content
       but keep the visibility badge fully legible so it can be toggled back. */
    .resource-item.hidden-from-students .row,
    .resource-item.hidden-from-students .resource-download {
        opacity: 0.35;
        filter: grayscale(0.5);
    }

    @keyframes loading {
        from {
            background-position-x: 0;
        }

        to {
            background-position-x: 42.5px;
        }
    }

    .resource-item.armed {
        background: repeating-linear-gradient(
            -45deg,
            #e9b817,
            #e9b817 15px,
            #ffce2c 15px,
            #ffce2c 30px
        );
        animation-name: loading;
        animation-duration: 0.5s;
        animation-iteration-count: infinite;
        animation-timing-function: linear;
    }

    .resource-item.armed .cancel-icon {
        display: flex;
    }

    .resource-item.active::after,
    .resource-item.armed::after {
        content: '';
        display: block;
        position: absolute;
        top: 0;
        right: 0;
        border-top: 2rem black solid;
        border-bottom: 2rem black solid;
        border-left: 1rem solid rgba(0, 0, 0, 0);
    }

    .resource-item span {
        font-size: 0.8rem;
        font-weight: 400;
        overflow: hidden;
        text-align: left;
    }

    .resource-item.active span,
    .resource-item.armed span {
        color: #161414;
        font-weight: 600;
    }

    .resource-item small {
        display: block;
        text-transform: uppercase;
        font-size: 0.75rem;
        font-weight: 600;
        color: rgb(74, 198, 255);
        text-align: left;
    }

    .resource-item.active small,
    .resource-item.armed small {
        color: white;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    }

    .thumbnail {
        position: relative;
        display: flex;
        flex-shrink: 0;
        cursor: pointer;
        width: 4rem;
        height: 4rem;
        border-radius: 0.25rem 0 0 0.25rem;
        margin-right: 0.5rem;
        background-color: #0f172a;
        border: none;
        overflow: hidden;
    }

    .thumbnail.placeholder {
        background-color: #0b1220;
        border: 1px solid rgba(148, 163, 184, 0.45);
        background-image: none !important;
        background-position: center center !important;
        background-repeat: no-repeat !important;
        background-size: 60% !important;
        box-shadow:
            inset 0 0 0 1px rgba(15, 23, 42, 0.35),
            0 8px 18px rgba(8, 47, 73, 0.45);
    }

    .thumbnail.placeholder::before {
        content: '';
        position: absolute;
        inset: 9px;
        background: linear-gradient(145deg, #fde68a 0%, #facc15 60%, #f59e0b 100%);
        mask: var(--placeholder-icon) center/contain no-repeat;
        -webkit-mask: var(--placeholder-icon) center/contain no-repeat;
        opacity: 0.92;
    }

    .row {
        display: flex;
        align-items: center;
        background-color: transparent;
        color: #ddd;
        padding-right: 10px;
    }
    /* Scope the hover/focus underline to the title span only. Applying it
       to the whole .row propagates a line through every inline child,
       including the visibility badge — and child text-decoration: none
       cannot un-paint an underline drawn by an ancestor. */
    .row:focus > span:not(.resource-visibility-slot),
    .row:hover > span:not(.resource-visibility-slot) {
        text-decoration: underline;
    }
    .row:focus-visible {
        z-index: 1;
    }

    .stretch {
        display: flex;
        flex-grow: 15;
        height: 100%;
    }

    .menu-icon {
        color: #ddd;
        font-size: 1.3rem;
        padding: 0.4rem;
        border-radius: 10rem;
        margin-right: 0.25rem;
        z-index: 100;
    }

    .menu-icon.interactive:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }

    .disabled {
        opacity: 0.25;
    }

    .cancel-icon {
        display: none;
        color: #ddd;
        background-color: rgba(0, 0, 0, 0.25);
        font-size: 1.1rem;
        padding: 0.3rem;
        border-radius: 10rem;
        margin-right: 0.25rem;
        box-shadow: 0rem 0.1rem 0.2rem rgba(0, 0, 0, 0.125) inset;
        z-index: 100;
    }

    .cancel-icon.interactive:hover {
        background-color: rgba(0, 0, 0, 0.5);
    }


    .resource-download {
        margin-right: 15px;
        background: transparent;
        color: #ffffff;

        img {
            width: 1.5rem;
            height: 1.5rem;
        }
    }

    .resource-item.active .resource-download {
        color: #000000;
    }

    .resource-download.preview-disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }

    .resource-item.active:hover .resource-download {
        color: #000000;
    }

    .resource-item .currently-not-available {
        font-weight: bold;
        background: rgb(247, 244, 231);
        padding: 5px;
        display: inline-block;
        border-radius: 0.5rem;
        color: #000000 !important;
    }
</style>
